import { supabase } from '../lib/supabase';

/**
 * Schedule Service
 * Provides CRUD operations for project_tasks table.
 */

/**
 * Chaine vide -> null, pour les colonnes que Postgres refuse de lire en ''.
 *
 * Motif repris de subcontractorService.js:154-157. PAS `|| null` : celui-ci
 * transformerait aussi 0 et false en null. Ici, seule la chaine vide compte.
 */
const emptyToNull = (v) => (v === '' ? null : v);

/**
 * List all tasks for a given project, in the user's order (sort_order).
 * @param {string} projectId - UUID of the project
 * @returns {Promise<{data: Array, error: object|null}>}
 */
export async function listTasksByProject(projectId) {
  const { data, error } = await supabase?.from('project_tasks')?.select(`
      id,
      project_id,
      parent_id,
      subcontractor_id,
      name,
      start_date,
      end_date,
      duration_days,
      progress,
      status,
      color,
      sort_order,
      created_at,
      updated_at
    `)?.eq('project_id', projectId)?.order('sort_order', { ascending: true });

  return { data, error };
}

/**
 * Create a new task.
 * duration_days is auto-calculated by the database trigger.
 * @param {object} taskData
 * @param {string} taskData.project_id
 * @param {string|null} [taskData.parent_id]
 * @param {string|null} [taskData.subcontractor_id]
 * @param {string} taskData.name
 * @param {string} taskData.start_date - ISO date string (YYYY-MM-DD)
 * @param {string} taskData.end_date   - ISO date string (YYYY-MM-DD)
 * @param {number} [taskData.progress]
 * @param {string|null} [taskData.color]
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function createTask(taskData) {
  const { project_id, parent_id = null, subcontractor_id = null, name, start_date, end_date, progress = 0, color = null, sort_order = 0 } = taskData;

  const { data, error } = await supabase?.from('project_tasks')?.insert({
      project_id,
      parent_id,
      subcontractor_id,
      name,
      // Aujourd'hui SchedulePage refuse une date vide avant d'arriver ici. Le
      // service, lui, etait nu : un second appelant sans cette verification
      // aurait leve le meme "invalid input syntax for type date".
      start_date: emptyToNull(start_date),
      end_date: emptyToNull(end_date),
      progress,
      color,
      sort_order,
    })?.select()?.single();

  return { data, error };
}

/**
 * Update an existing task by ID.
 * duration_days is auto-recalculated by the database trigger when dates change.
 * @param {string} taskId - UUID of the task to update
 * @param {object} updates - Fields to update
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function updateTask(taskId, updates) {
  // Meme precaution que createTask, sur les seules colonnes date. Les autres
  // champs traversent inchanges.
  const payload = { ...updates };
  if ('start_date' in payload) payload.start_date = emptyToNull(payload?.start_date);
  if ('end_date' in payload) payload.end_date = emptyToNull(payload?.end_date);

  const { data, error } = await supabase?.from('project_tasks')?.update(payload)?.eq('id', taskId)?.select()?.single();

  return { data, error };
}

/**
 * Delete a task by ID.
 * Child tasks (parent_id = taskId) are cascade-deleted by the database.
 * @param {string} taskId - UUID of the task to delete
 * @returns {Promise<{error: object|null}>}
 */
export async function deleteTask(taskId) {
  const { error } = await supabase?.from('project_tasks')?.delete()?.eq('id', taskId);

  return { error };
}

// ─── Task Dependencies ───────────────────────────────────────────────────────

/**
 * List all dependencies for tasks in a project.
 * @param {string} projectId
 * @returns {Promise<{data: Array, error: object|null}>}
 */
export async function listDependenciesByProject(projectId) {
  // Get all task ids for this project first, then fetch dependencies
  const { data: tasks, error: tasksErr } = await supabase
    ?.from('project_tasks')
    ?.select('id')
    ?.eq('project_id', projectId);

  if (tasksErr) return { data: [], error: tasksErr };

  const taskIds = (tasks || [])?.map((t) => t?.id);
  if (taskIds?.length === 0) return { data: [], error: null };

  const { data, error } = await supabase
    ?.from('task_dependencies')
    ?.select('id, task_id, depends_on_task_id, type')
    ?.in('task_id', taskIds);

  return { data: data || [], error };
}

/**
 * Create a finish-to-start dependency between two tasks.
 * @param {string} taskId - the dependent task (Task B)
 * @param {string} dependsOnTaskId - the predecessor task (Task A)
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function createDependency(taskId, dependsOnTaskId, type = 'FS') {
  const { data, error } = await supabase
    ?.from('task_dependencies')
    ?.insert({ task_id: taskId, depends_on_task_id: dependsOnTaskId, type })
    ?.select()
    ?.single();

  return { data, error };
}

/**
 * Delete a dependency by id.
 * @param {string} dependencyId
 * @returns {Promise<{error: object|null}>}
 */
export async function deleteDependency(dependencyId) {
  const { error } = await supabase
    ?.from('task_dependencies')
    ?.delete()
    ?.eq('id', dependencyId);

  return { error };
}

/**
 * Batch update sort_order for multiple tasks.
 * @param {Array<{id: string, sort_order: number}>} updates
 * @returns {Promise<{error: object|null}>}
 */
export async function batchUpdateSortOrder(updates) {
  const results = await Promise.all(
    (updates || [])?.map(({ id, sort_order }) =>
      supabase?.from('project_tasks')?.update({ sort_order })?.eq('id', id)
    )
  );
  const firstError = results?.find((r) => r?.error);
  return { error: firstError?.error || null };
}
